import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Phone, Mail, MoreHorizontal } from 'lucide-react';
import { NotionSelect, type NotionSelectOption } from './NotionSelect';
import { NotionDatePicker } from './NotionDatePicker';
import { NotionTimePicker } from './NotionTimePicker';
import Drawer from '../ui/Drawer';
import { Eyebrow, Field, Mono, Pill } from '../ui/kit';
import type { Booking } from '../../types';

interface BookingDetailDrawerProps {
  booking: Booking | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (updatedBooking: Booking) => void;
  onDelete?: (id: string) => void;
}

/**
 * A pending booking asks one question — are you taking this job? — and the
 * answer is a button, not a dropdown three sections down. Confirm, decline and
 * call sit in the footer where they can't scroll away; the schedule fields
 * below are for the case where you're taking it but not at that hour.
 *
 * Everything the customer supplied (their name, number, what they asked for)
 * stays editable, because the common repair is a mistyped phone number — but
 * it's laid out as their request rather than as a form you're filling in.
 */

const STATUS_OPTIONS: NotionSelectOption[] = [
  { value: 'pending', label: 'Pending', tone: 'warn' },
  { value: 'confirmed', label: 'Confirmed', tone: 'live' },
  { value: 'completed', label: 'Completed', tone: 'info' },
  { value: 'cancelled', label: 'Cancelled', tone: 'danger' },
];

const DURATION_OPTIONS: NotionSelectOption[] = [
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' },
];

interface Draft {
  serviceType: string;
  customerName: string;
  status: Booking['status'];
  date: string;
  time: string;
  duration: number;
  notes: string;
}

function toDraft(booking: Booking | null): Draft {
  return {
    serviceType: booking?.serviceType ?? '',
    customerName: booking?.customerName ?? '',
    status: booking?.status ?? 'pending',
    date: booking?.date ?? '',
    time: booking?.time ?? '',
    duration: booking?.duration ?? 60,
    notes: booking?.notes ?? '',
  };
}

const BookingDetailDrawer: React.FC<BookingDetailDrawerProps> = ({
  booking,
  isOpen,
  onClose,
  onSave,
  onDelete,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [draft, setDraft] = useState<Draft>(() => toDraft(booking));

  /*
    Re-fill the draft only when a *different* booking is shown.

    The pair of effects this replaces — one pushing the prop into per-field
    state, one diffing that state back against the prop and calling `onSave` —
    both ran in the same commit, so the diff compared the incoming booking with
    the still-empty draft and saved blanks over the record on open. See the same
    note in LeadDetailModal; it wiped three leads on the demo account.
  */
  const hydratedIdRef = useRef<string | undefined>(booking?.id);
  useEffect(() => {
    if (booking && booking.id !== hydratedIdRef.current) {
      hydratedIdRef.current = booking.id;
      setDraft(toDraft(booking));
      setShowMenu(false);
      setShowDeleteConfirm(false);
    }
  }, [booking]);

  // Auto-resize the note field to fit what's in it.
  useEffect(() => {
    const textarea = notesTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [draft.notes]);

  /** Apply one field edit and hand the whole record up. The caller debounces. */
  const commit = (patch: Partial<Draft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (!booking || !onSave) return;
    onSave({ ...booking, ...next });
  };

  if (!booking) return null;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    return `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  const handleConfirmDelete = () => {
    if (onDelete) onDelete(booking.id);
    setShowDeleteConfirm(false);
    onClose();
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Booking"
      title={draft.serviceType || 'Appointment'}
      subtitle={
        draft.date ? `${formatDate(draft.date)} at ${formatTime(draft.time)}` : 'Not scheduled'
      }
      width="xl"
      headerActions={
        onDelete && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowMenu(open => !open)}
              aria-label="More actions"
              className="rounded-full p-2 text-zinc-400 transition-colors hover:bg-zinc-900/5 hover:text-primary"
            >
              <MoreHorizontal size={18} />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full z-20 mt-1 w-44 rounded-lg border border-zinc-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMenu(false);
                      setShowDeleteConfirm(true);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-700 transition-colors hover:bg-zinc-100"
                  >
                    Delete booking
                  </button>
                </div>
              </>
            )}
          </div>
        )
      }
      footer={
        <div className="space-y-3">
          {/*
            The decision, when there is one. A confirmed booking has nothing
            left to decide, so the row collapses to the ways of reaching them.
          */}
          {draft.status === 'pending' && (
            <div className="flex gap-2">
              <Pill onClick={() => commit({ status: 'confirmed' })} className="flex-1">
                Confirm booking
              </Pill>
              <Pill tone="danger" onClick={() => commit({ status: 'cancelled' })} className="flex-1">
                Decline
              </Pill>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {booking.customerPhone && (
              <Pill
                href={`tel:${booking.customerPhone}`}
                tone={draft.status === 'pending' ? 'outline' : 'solid'}
              >
                <Phone size={15} />
                Call {draft.customerName.split(' ')[0] || 'customer'}
              </Pill>
            )}
            {booking.customerEmail && (
              <Pill href={`mailto:${booking.customerEmail}`} tone="outline">
                <Mail size={15} />
                Email
              </Pill>
            )}
            {draft.status === 'confirmed' && (
              <Pill tone="ghost" onClick={() => commit({ status: 'completed' })}>
                Mark completed
              </Pill>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-8">
        {/* Who booked, and how to reach them. */}
        <section>
          {/*
            No status chip here. It sat on the Customer rule — labelling the
            wrong section — and repeated the Status field a screen below it in
            a different chip design. The field is the one place status is both
            shown and set.
          */}
          <Eyebrow className="mb-3">Customer</Eyebrow>
          <div className="rounded-xl border border-zinc-200 bg-paper-tint">
            <dl className="divide-y divide-zinc-200">
              <div className="flex gap-4 px-4 py-3">
                <dt className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                  Name
                </dt>
                <dd className="min-w-0 flex-1 text-[14px] text-primary">{draft.customerName}</dd>
              </div>
              <div className="flex gap-4 px-4 py-3">
                <dt className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                  Phone
                </dt>
                <dd className="min-w-0 flex-1">
                  <a
                    href={`tel:${booking.customerPhone}`}
                    className="text-[14px] text-primary underline-offset-4 hover:underline"
                  >
                    {booking.customerPhone}
                  </a>
                </dd>
              </div>
              {booking.customerEmail && (
                <div className="flex gap-4 px-4 py-3">
                  <dt className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                    Email
                  </dt>
                  <dd className="min-w-0 flex-1">
                    <a
                      href={`mailto:${booking.customerEmail}`}
                      className="break-all text-[14px] text-primary underline-offset-4 hover:underline"
                    >
                      {booking.customerEmail}
                    </a>
                  </dd>
                </div>
              )}
              {booking.createdAt && (
                <div className="flex gap-4 px-4 py-3">
                  <dt className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                    Requested
                  </dt>
                  <dd className="min-w-0 flex-1">
                    <Mono>
                      {new Date(booking.createdAt).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Mono>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </section>

        {/* The slot itself — the part you'd change when you accept but can't make it. */}
        <section className="space-y-5">
          <Eyebrow>Appointment</Eyebrow>
          <Field label="Service" htmlFor="booking-service">
            <input
              id="booking-service"
              value={draft.serviceType}
              onChange={e => commit({ serviceType: e.target.value })}
              maxLength={512}
              placeholder="What the job is"
              className="input-field"
            />
          </Field>
          {/*
            Every control in this section is a `Field` over an input-shaped box,
            pickers included — they used to be borderless inline buttons, which
            left the bordered Service input above them looking like the only
            real field on the panel.
          */}
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Date">
              <NotionDatePicker value={draft.date} onChange={value => commit({ date: value })} />
            </Field>
            <Field label="Time">
              <NotionTimePicker value={draft.time} onChange={value => commit({ time: value })} />
            </Field>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Duration">
              <NotionSelect
                value={draft.duration}
                onChange={value => commit({ duration: value as number })}
                options={DURATION_OPTIONS}
              />
            </Field>
            <Field label="Status">
              <NotionSelect
                value={draft.status}
                onChange={value => commit({ status: value as Booking['status'] })}
                options={STATUS_OPTIONS}
                showAsTag
              />
            </Field>
          </div>
        </section>

        <section>
          <Eyebrow className="mb-3">Notes</Eyebrow>
          <textarea
            ref={notesTextareaRef}
            value={draft.notes}
            onChange={e => commit({ notes: e.target.value })}
            placeholder="Access instructions, what to bring, what was agreed…"
            className="input-field resize-none overflow-hidden leading-relaxed"
            style={{ minHeight: '5rem' }}
          />
          <p className="mt-2 text-[13px] text-muted">
            Includes anything the customer typed when they booked.
          </p>
        </section>
      </div>

      {/*
        Portalled out of the panel: the drawer animates on a transform, and a
        transformed ancestor becomes the containing block for `fixed` children —
        the dialog would centre itself inside the drawer instead of the screen.
      */}
      {showDeleteConfirm && createPortal(
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-6">
            <h3 className="font-display text-lg font-bold tracking-tight text-primary">
              Delete this booking?
            </h3>
            <p className="mt-2 text-[14px] leading-relaxed text-muted">
              The appointment for <strong className="text-primary">{draft.customerName}</strong>{' '}
              will be removed for good. Declining instead keeps the record and marks it cancelled.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Pill tone="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>
                Keep it
              </Pill>
              <Pill tone="danger" size="sm" onClick={handleConfirmDelete}>
                Delete
              </Pill>
            </div>
          </div>
        </div>,
        document.body
      )}
    </Drawer>
  );
};

export default BookingDetailDrawer;
